import React from 'react';
import { ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceArea, ReferenceLine, Legend, Label } from 'recharts';
import { FigureCard } from './FigureCard';
const times = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 110, 120];
const hot = [0.28, 0.54, 0.88, 1.24, 1.52, 1.68, 1.65, 1.55, 1.44, 1.33, 1.21, 1.1, 0.98];
const mid = [0.176, 0.336, 0.504, 0.706, 0.81, 0.865, 0.828, 0.774, 0.723, 0.672, 0.624, 0.563, 0.504];
const low = [0.06, 0.09, 0.13, 0.17, 0.19, 0.2, 0.19, 0.18, 0.17, 0.16, 0.15, 0.14, 0.13];
const data = times.map((t, i) => ({
  t,
  p100: hot[i],
  p80: mid[i],
  p60: low[i]
}));
export function Figure3() {
  return <FigureCard number={3} title="Time-Varying Power P(t) at Three Heat Source Temperatures" subtitle="Shaded area under each curve = E_out (total electrical energy), calculated via the Trapezoidal Rule." caption="All three temperatures share the same three phases: pressure build-up, peak steam flow, and decay as the heat source cools and steam pressure falls. Higher T_hot produces a larger peak and a larger area under the curve." data-id="element-139">
      <div className="relative" data-id="element-140">
        <div className="h-[460px] w-full" data-id="element-141">
          <ResponsiveContainer data-id="element-142">
            <ComposedChart data={data} margin={{
            top: 30,
            right: 30,
            left: 30,
            bottom: 50
          }} data-id="element-143">
              <defs data-id="element-144">
                <linearGradient id="f3Hot" x1="0" y1="0" x2="0" y2="1" data-id="element-145">
                  <stop offset="0%" stopColor="#8B2500" stopOpacity={0.28} data-id="element-146" />
                  <stop offset="100%" stopColor="#8B2500" stopOpacity={0.02} data-id="element-147" />
                </linearGradient>
                <linearGradient id="f3Mid" x1="0" y1="0" x2="0" y2="1" data-id="element-148">
                  <stop offset="0%" stopColor="#1A4A68" stopOpacity={0.26} data-id="element-149" />
                  <stop offset="100%" stopColor="#1A4A68" stopOpacity={0.02} data-id="element-150" />
                </linearGradient>
                <linearGradient id="f3Low" x1="0" y1="0" x2="0" y2="1" data-id="element-151">
                  <stop offset="0%" stopColor="#2E5E28" stopOpacity={0.26} data-id="element-152" />
                  <stop offset="100%" stopColor="#2E5E28" stopOpacity={0.02} data-id="element-153" />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#E5E0D6" strokeDasharray="2 4" data-id="element-154" />

              {/* Phase bands */}
              <ReferenceArea x1={0} x2={40} fill="#FEF3C7" fillOpacity={0.5} data-id="element-155" />
              <ReferenceArea x1={40} x2={50} fill="#FED7AA" fillOpacity={0.5} data-id="element-156" />
              <ReferenceArea x1={50} x2={120} fill="#DBEAFE" fillOpacity={0.5} data-id="element-157" />

              <XAxis dataKey="t" type="number" domain={[0, 130]} ticks={[0, 20, 40, 60, 80, 100, 120]} axisLine={{
              stroke: '#1B1B1B'
            }} tick={{
              fontSize: 12,
              fill: '#374151'
            }} data-id="element-158">
                <Label value="Time, t (seconds)" position="bottom" offset={20} style={{
                fill: '#1B1B1B',
                fontSize: 13,
                fontFamily: 'Inter',
                fontWeight: 500
              }} data-id="element-159" />
              </XAxis>
              <YAxis domain={[0, 1.9]} ticks={[0, 0.25, 0.5, 0.75, 1.0, 1.25, 1.5, 1.75]} axisLine={{
              stroke: '#1B1B1B'
            }} tick={{
              fontSize: 12,
              fill: '#374151'
            }} data-id="element-160">
                <Label value="Instantaneous Electrical Power, P(t) (μW)" angle={-90} position="left" offset={10} style={{
                fill: '#1B1B1B',
                fontSize: 13,
                fontFamily: 'Inter',
                fontWeight: 500,
                textAnchor: 'middle'
              }} data-id="element-161" />
              </YAxis>
              <Tooltip contentStyle={{
              background: '#fff',
              border: '1px solid #1B1B1B',
              fontFamily: 'Inter',
              fontSize: 12
            }} labelFormatter={l => `t = ${l} s`} data-id="element-162" />

              {/* Phase dividers */}
              <ReferenceLine x={40} stroke="#9B9384" strokeDasharray="4 4" data-id="element-163" />
              <ReferenceLine x={50} stroke="#9B9384" strokeDasharray="4 4" data-id="element-164" />

              <Area type="monotone" dataKey="p100" stroke="none" fill="url(#f3Hot)" isAnimationActive={false} data-id="element-165" />
              <Area type="monotone" dataKey="p80" stroke="none" fill="url(#f3Mid)" isAnimationActive={false} data-id="element-166" />
              <Area type="monotone" dataKey="p60" stroke="none" fill="url(#f3Low)" isAnimationActive={false} data-id="element-167" />

              <Line type="monotone" dataKey="p100" name="100 °C" stroke="#8B2500" strokeWidth={2.2} dot={{
              r: 3,
              fill: '#8B2500'
            }} isAnimationActive={false} data-id="element-168" />
              <Line type="monotone" dataKey="p80" name="80 °C" stroke="#1A4A68" strokeWidth={2.2} dot={{
              r: 3,
              fill: '#1A4A68'
            }} isAnimationActive={false} data-id="element-169" />
              <Line type="monotone" dataKey="p60" name="60 °C" stroke="#2E5E28" strokeWidth={2.2} dot={{
              r: 3,
              fill: '#2E5E28'
            }} isAnimationActive={false} data-id="element-170" />

              <Legend verticalAlign="top" align="right" iconType="line" wrapperStyle={{
              fontFamily: 'Inter',
              fontSize: 12,
              paddingBottom: 10
            }} data-id="element-171" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* Phase labels overlay */}
        <div className="grid grid-cols-12 gap-0 -mt-3 px-[60px] text-center font-sans text-[10px]" data-id="element-172">
          <div className="col-span-4 text-[#7C5A00]" data-id="element-173">
            <div className="uppercase tracking-wider font-semibold" data-id="element-174">
              Phase 1 · 0–40s
            </div>
            <div className="italic" data-id="element-175">Steam pressure building</div>
          </div>
          <div className="col-span-1 text-[#B45309]" data-id="element-176">
            <div className="uppercase tracking-wider font-semibold" data-id="element-177">P2</div>
            <div className="italic" data-id="element-178">Peak</div>
          </div>
          <div className="col-span-7 text-[#1E40AF]" data-id="element-179">
            <div className="uppercase tracking-wider font-semibold" data-id="element-180">
              Phase 3 · 50–120s
            </div>
            <div className="italic" data-id="element-181">Decay (cooling + steam loss)</div>
          </div>
        </div>

        {/* Formula + annotations */}
        <div className="grid grid-cols-3 gap-3 mt-6 mx-2" data-id="element-182">
          <div className="bg-white border-2 border-[#1B1B1B] rounded-sm p-3" data-id="element-183">
            <div className="font-sans text-[9px] uppercase tracking-wider text-[#1B1B1B] font-semibold mb-1" data-id="element-184">
              Trapezoidal Rule
            </div>
            <div className="font-mono text-[12px] text-[#1B1B1B] leading-relaxed" data-id="element-185">
              E<sub data-id="element-186">out</sub> = Σᵢ [(Pᵢ + Pᵢ₊₁)/2] × Δt
            </div>
            <div className="font-mono text-[10px] text-[#5B5448] mt-1" data-id="element-187">
              Δt = 10 s · 12 trapezoids per trial
            </div>
          </div>
          <div className="bg-[#FEF3C7] border border-[#EF9F27] rounded-sm p-3" data-id="element-188">
            <div className="font-sans text-[9px] uppercase tracking-wider text-[#B45309] font-semibold mb-1" data-id="element-189">
              Peak — 100 °C
            </div>
            <div className="font-mono text-[12px] text-[#8B2500] font-semibold" data-id="element-190">
              1.68 μW at t = 50 s
            </div>
            <div className="font-serif italic text-[10px] text-[#5B5448] mt-1" data-id="element-191">
              Area under curve = ∫P(t)dt = E_out
            </div>
          </div>
          <div className="bg-[#EEF4F8] border border-[#1A4A68]/40 rounded-sm p-3" data-id="element-192">
            <div className="font-sans text-[9px] uppercase tracking-wider text-[#1A4A68] font-semibold mb-1" data-id="element-193">
              Area — 80 °C
            </div>
            <div className="font-mono text-[12px] text-[#1A4A68] font-semibold" data-id="element-194">
              E<sub data-id="element-195">out</sub> = 77.5 μJ
            </div>
            <div className="font-serif italic text-[10px] text-[#5B5448] mt-1" data-id="element-196">
              Sum of 12 trapezoids (see Fig. 6)
            </div>
          </div>
        </div>
      </div>
    </FigureCard>;
}